/**
 * The body of one event page (`/about-us/news-events/<slug>`): title, description and call to
 * action over the hero image, the "General Event Information" block (display dates, location and
 * a Google map), "How To Sign Up", and a sidebar with the RSVP box, a contact link, the next two
 * upcoming events, "Related Posts" and the newsletter sign-up.
 *
 * Rendered only by `./EventDetailsContainer.tsx`, which sets the page's meta tags and passes the
 * current language's event. The event fields come from
 * `src/contentful/event/getContentfulEventDetails.ts`; the fixed labels from `./Content.tsx`.
 *
 * What shows depends on the optional Contentful fields:
 * - "For more information visit <label>" needs both `thirdPartyLinkLabel` and `thirdPartyLinkUrl`.
 * - The external action button needs both `externalAction` and `externalActionUrl`.
 * - The RSVP box and the "RSVP with Ryerson" button need `internalAction`; the button also needs
 *   `internalActionUrl`.
 * - The map needs `mapLocation` with a non-zero `lat` and `lon`. It uses
 *   `components/Locations/Map/Map.tsx` with `NEXT_PUBLIC_GOOGLE_MAPS_KEY`.
 *
 * As the code stands:
 * - The "Upcoming events" sidebar entries aren't links, and can include the event being viewed.
 * - "Related Posts" is `components/Blog/PopularContent.tsx` with no slug excluded; its header
 *   notes that its mobile links resolve under this page's path.
 * - Below `lg`, a "Register Today" button at the end of the information section has an empty
 *   click handler and no link.
 * - The breadcrumb is built by `@ryerson/frontend.navigation` → `Breadcrumb` from the URL path.
 * - The scroll handler that moves the vertical social bar is added on mount and never removed.
 *   `components/NewsEvents/News/NewsArticle.tsx` and `components/Blog/Blog.tsx` hold the same
 *   handler.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Accordion, AccordionTab } from "@ryerson/frontend.expansion";
import { Link, ButtonLink } from "@ryerson/frontend.navigation";
import { Breadcrumb } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { Media } from "@ryerson/frontend.next";
import { useTheme } from "@ryerson/frontend.theme";
import SocialMedia from "@components/NewsEvents/SocialMedia/SocialMedia";
import { Button } from "@ryerson/frontend.button";
import styled from "@emotion/styled";
import Map from "@components/Locations/Map/Map";
import { PopularContent } from "@components/Blog/PopularContent";
import { css } from "@emotion/react";
import StayTunedComponent from "@components/Shared/StayTuned/StayTuned";
import {
	ProcessedContentfulEventDetailsFields,
	ProcessedContentfulUpcomingEventsByLanguage,
} from "@contentful/event/types";
import { TranslatedBlogEntries } from "@contentful/blog/types";
import { StaticContent } from "./Content";
import Spacer from "@components/Shared/Spacer/Spacer";
const MobileHeroImage = styled.div`
	display: block;
	width: 100%;
	margin-top: 34px;
	height: 190px;
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center center;
`;

const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 0px;
			}
		`;
	}}
`;

const Divider = styled.hr`
	margin-top: 0px;
	margin-bottom: 0px;
	opacity: 0.1;
`;

const HeroLeft = styled.div`
	display: inline-block;
	vertical-align: top;
	width: calc(100% - 350px);
`;

const HeroRight = styled.div`
	display: inline-block;
	vertical-align: top;
	width: 350px;
	padding-top: 85px;
	box-sizing: border-box;
	text-align: right;
`;

const Left = styled.div`
	display: inline-block;
	width: calc(100% - 300px);
	position: relative;
	z-index: 1;
	vertical-align: top;
	padding: 51px 40px 73px 40px;
	box-sizing: border-box;
	height: auto;
	border-radius: 2px;
`;

const Right = styled.div`
	display: inline-block;
	width: 290px;
	vertical-align: top;
	position: relative;
	z-index: 1;
	margin-left: 10px;
`;

const RightContainer = styled.div`
	display: block;
	width: 100%;
	height: auto;
	padding: 15px 22px 24px 22px;
	box-sizing: border-box;
	border-radius: 2px;
`;

const Social = styled.div`
	width: 20px;
	height: auto;
	left: -36px;
	position: absolute;
`;

const HeroImage = styled.div`
	display: block;
	width: 100%;
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center center;
	height: 440px;
	position: absolute;
	left: 0;
`;

const MapContainer = styled.div`
	display: block;
	width: 100%;
	border-radius: 2px;
	height: 340px;
	overflow: hidden;
`;

const UpcomingEvent = styled.div`
	display: block;
	width: 100%;
	height: 102px;
`;

const UpcomingEventImage = styled.div`
	display: inline-block;
	width: 80px;
	background-repeat: no-repeat;
	background-position: right center;
	background-size: cover;
	vertical-align: top;
	height: 102px;
`;

const UpcomingEventDetails = styled.div`
	display: inline-block;
	width: calc(100% - 80px);
	padding: 20px 0px;
	vertical-align: top;
	height: 102px;
	box-sizing: border-box;
`;

// The GA `stay_tuned` event value for sign-ups from an event page (see
// `Shared/StayTuned/StayTuned.tsx`).
const stayTunedParent = "Event";

type EventDetailsProps = {
	/** The event in the current language, picked by `./EventDetailsContainer.tsx`. */
	contentfulEventDetailsForLanguage: ProcessedContentfulEventDetailsFields;
	/** The build-time upcoming-events list, for the sidebar. */
	allUpcomingEventsByLanguage: ProcessedContentfulUpcomingEventsByLanguage;
	/** Metal Market Intelligence articles, for "Related Posts". */
	contentfulBlogEntries: TranslatedBlogEntries;
};

/** Renders one event page body for desktop (`lg` and up) and mobile; see the file header. */
const EventDetails: React.FC<EventDetailsProps> = ({
	contentfulEventDetailsForLanguage,
	allUpcomingEventsByLanguage,
	contentfulBlogEntries,
}) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const [socialReference, setSocialReference] = React.useState<any>(React.createRef());
	const [socialTop, setSocialTop] = React.useState<number>(0);
	const content = StaticContent[language];

	// Keeps the vertical social bar level with the viewport while the body (`Left`) scrolls past,
	// pinned at the top before it and near the bottom after it. The listener is never removed.
	React.useEffect(() => {
		if (window !== undefined && document !== undefined) {
			window.addEventListener("scroll", function () {
				if (socialReference.current) {
					let offset = socialReference.current.getBoundingClientRect();
					let startingTop = offset.top - document.body.getBoundingClientRect().top;
					if (
						window.scrollY + 120 >= startingTop &&
						window.scrollY + 265 < socialReference.current.offsetHeight + startingTop
					) {
						setSocialTop(window.scrollY - startingTop + 120);
					} else if (
						socialReference.current.offsetHeight + startingTop <=
						window.scrollY + 265
					) {
						setSocialTop(socialReference.current.offsetHeight - 145);
					} else {
						setSocialTop(0);
					}
				}
			});
		}
		if (!socialReference) setSocialReference(React.createRef());
	}, []);

	// Same test as `./UpcomingEventsSection.tsx`: `date` read as UTC midnight against the visitor's
	// clock, so an event that has started isn't shown, though events are meant to stay listed while
	// they run. The event being viewed isn't excluded.
	const upcomingEventsToDisplay = allUpcomingEventsByLanguage[language]
		.filter((event) => new Date(event.date) > new Date())
		.slice(0, 2); // only display the first 2 upcoming events

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary" vPadding="20px">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<HeroLeft>
						<Typography
							variant="h1"
							css={css`
								margin-top: 20px;
								margin-bottom: 30px;
								display: block;
							`}
							type="tertiary"
						>
							{contentfulEventDetailsForLanguage.title}
						</Typography>
						<Media lessThan="xl">
							<SocialMedia orientation="horizontal" type="tertiary" />
							<Spacer />
							<Spacer />
						</Media>
						{contentfulEventDetailsForLanguage.description &&
						contentfulEventDetailsForLanguage.description.length > 0 ? (
							<Typography
								css={css`
									margin-bottom: 20px;
								`}
								type="tertiary"
								size="lg"
								variant="div"
							>
								{contentfulEventDetailsForLanguage.description}
							</Typography>
						) : (
							<></>
						)}
						{contentfulEventDetailsForLanguage.callToAction &&
						contentfulEventDetailsForLanguage.callToAction.length > 0 ? (
							<Typography type="tertiary" size="md" variant="div">
								{contentfulEventDetailsForLanguage.callToAction}
							</Typography>
						) : (
							<></>
						)}
						<Spacer
							css={css`
								height: 90px;
							`}
						/>
					</HeroLeft>
					<HeroRight>
						{contentfulEventDetailsForLanguage.thirdPartyLinkLabel &&
						contentfulEventDetailsForLanguage.thirdPartyLinkUrl ? (
							<Typography
								css={css`
									margin-bottom: 25px;
								`}
								variant="div"
								size="md"
								type="tertiary"
							>
								{content.moreInformation}{" "}
								<Link
									next={false}
									to={contentfulEventDetailsForLanguage.thirdPartyLinkUrl}
									type="tertiary"
								>
									{contentfulEventDetailsForLanguage.thirdPartyLinkLabel}
								</Link>
							</Typography>
						) : (
							<></>
						)}
						{contentfulEventDetailsForLanguage.externalAction &&
						contentfulEventDetailsForLanguage.externalActionUrl ? (
							<ButtonLink
								orientation="right"
								type="tertiary"
								label={contentfulEventDetailsForLanguage.externalAction}
								to={contentfulEventDetailsForLanguage.externalActionUrl}
								next={false}
								linkType="external"
							/>
						) : (
							<></>
						)}
					</HeroRight>
				</ContentSection>
				<ContentSection type="secondary" vPadding="0px">
					<HeroImage
						css={css`
							background-image: url(${contentfulEventDetailsForLanguage.heroImage
								?.file
								? contentfulEventDetailsForLanguage.heroImage?.file?.url
								: ""});
						`}
					/>
					<Spacer
						css={css`
							height: 100px;
						`}
					/>

					<Left
						ref={socialReference}
						css={css`
							background-color: ${theme.colors.primary.background};
						`}
					>
						<Media greaterThan="lg">
							<Social
								css={css`
									top: ${socialTop + "px"};
								`}
							>
								<SocialMedia orientation="vertical" type="tertiary" />
							</Social>
						</Media>
						<Typography
							variant="h2"
							type="primary"
							css={css`
								margin-bottom: 35px;
							`}
						>
							{content.generalTitle}
						</Typography>
						<Divider
							css={css`
								color: ${theme.colors.primary.darkerGray};
								margin-top: 0px;
								margin-bottom: 35px;
							`}
						/>
						<Flex
							css={css`
								width: calc(100% - 85px);
								margin-bottom: 16px;
							`}
							direction="row"
							justifyContent="space-between"
						>
							<FlexItem>
								<Typography variant="div" size="md" type="primary">
									{content.dateLabel}
									{": "}
									<Typography
										variant="span"
										size="md"
										color={theme.colors.primary.header}
										weight="bold"
									>
										{contentfulEventDetailsForLanguage.eventDates}
									</Typography>
								</Typography>
							</FlexItem>
							<FlexItem>
								<Typography variant="div" size="md" type="primary">
									{content.locationLabel}
									{": "}
									<Typography
										variant="span"
										size="md"
										color={theme.colors.primary.header}
										weight="bold"
									>
										{contentfulEventDetailsForLanguage.eventLocation}
									</Typography>
								</Typography>
							</FlexItem>
						</Flex>
						{contentfulEventDetailsForLanguage.mapLocation &&
						contentfulEventDetailsForLanguage.mapLocation.lon &&
						contentfulEventDetailsForLanguage.mapLocation.lat ? (
							<MapContainer>
								<Map
									googleKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY ?? ""}
									mapData={[
										{
											location: {
												lat: contentfulEventDetailsForLanguage.mapLocation
													.lat,
												lng: contentfulEventDetailsForLanguage.mapLocation
													.lon,
											},
											name: contentfulEventDetailsForLanguage.eventLocation,
											id: contentfulEventDetailsForLanguage.eventLocation,
										},
									]}
									zoomLevel={16}
								/>
							</MapContainer>
						) : (
							<></>
						)}
						<Typography
							variant="h2"
							type="primary"
							css={css`
								margin-top: 72px;
								margin-bottom: 40px;
							`}
						>
							{content.howToSignUp}
						</Typography>
						<Flex
							css={css`
								max-width: 616px;
							`}
							direction="row"
							justifyContent="space-between"
						>
							<FlexItem>
								{/* One step only: the internal action when set, otherwise the
								external action. The nested step "2" below repeats the condition
								that just failed, so it never renders. */}
								{contentfulEventDetailsForLanguage.internalAction ? (
									<>
										<Typography variant="div" type="primary" size="sm">
											<Typography
												variant="span"
												color={theme.colors.primary.header}
												weight="bolder"
												css={css`
													font-size: 20px;
													margin-right: 10px;
												`}
											>
												{"1"}
											</Typography>

											<Typography variant="span" type="primary" size="sm">
												{contentfulEventDetailsForLanguage.internalAction}
											</Typography>
											{contentfulEventDetailsForLanguage.internalActionUrl && (
												<Link
													to={
														contentfulEventDetailsForLanguage.internalActionUrl
													}
												>
													<Button
														css={css`
															margin-top: 16px;
														`}
														onClick={() => {}}
														size="xl"
														label={
															content.rsvp + " " + content.withRyerson
														}
													/>
												</Link>
											)}
										</Typography>
									</>
								) : (
									<>
										<FlexItem>
											{contentfulEventDetailsForLanguage.externalAction &&
											contentfulEventDetailsForLanguage.externalActionUrl ? (
												<Typography variant="div" type="primary" size="sm">
													<Typography
														variant="span"
														color={theme.colors.primary.header}
														weight="bolder"
														css={css`
															font-size: 20px;
															margin-right: 10px;
														`}
													>
														{"1"}
													</Typography>
													<Typography
														variant="span"
														type="primary"
														size="sm"
													>
														{
															contentfulEventDetailsForLanguage.externalAction
														}
													</Typography>
													<Link
														to={
															contentfulEventDetailsForLanguage.externalActionUrl
														}
													>
														<Button
															css={css`
																margin-top: 16px;
															`}
															onClick={() => {}}
															size="xl"
															label={content.registerToday}
														/>
													</Link>
												</Typography>
											) : (
												<>
													<FlexItem>
														{contentfulEventDetailsForLanguage.externalAction &&
														contentfulEventDetailsForLanguage.externalActionUrl ? (
															<Typography
																variant="div"
																type="primary"
																size="sm"
															>
																<Typography
																	variant="span"
																	color={
																		theme.colors.primary.header
																	}
																	weight="bolder"
																	css={css`
																		font-size: 20px;
																		margin-right: 10px;
																	`}
																>
																	{"2"}
																</Typography>
																<Typography
																	variant="span"
																	type="primary"
																	size="sm"
																>
																	{
																		contentfulEventDetailsForLanguage.externalAction
																	}
																</Typography>
																<Link
																	to={
																		contentfulEventDetailsForLanguage.externalActionUrl
																	}
																>
																	<Button
																		css={css`
																			margin-top: 16px;
																		`}
																		onClick={() => {}}
																		size="xl"
																		label={
																			content.registerToday
																		}
																	/>
																</Link>
															</Typography>
														) : (
															<></>
														)}
													</FlexItem>
												</>
											)}
										</FlexItem>
									</>
								)}
							</FlexItem>
						</Flex>
					</Left>
					<Right>
						{contentfulEventDetailsForLanguage.internalAction ? (
							<>
								<RightContainer
									css={css`
										background-color: ${theme.colors.primary.background};
										margin-bottom: 10px;
									`}
								>
									<Typography
										variant="h3"
										css={css`
											font-size: 20px;
											margin-bottom: 18px;
										`}
									>
										{content.rsvp}
									</Typography>
									<Divider
										css={css`
											color: ${theme.colors.primary.darkerGray};
											margin-top: 0px;
											margin-bottom: 24px;
										`}
									/>
									<Typography
										variant="div"
										size="xs"
										type="primary"
										css={css`
											margin-bottom: 10px;
										`}
									>
										{contentfulEventDetailsForLanguage.internalAction}
									</Typography>
									{contentfulEventDetailsForLanguage.internalActionUrl && (
										<Link
											to={contentfulEventDetailsForLanguage.internalActionUrl}
										>
											<Button
												size="lg"
												fullwidth={true}
												label={content.rsvp + " " + content.withRyerson}
												onClick={() => {}}
											/>
										</Link>
									)}
								</RightContainer>
							</>
						) : (
							<></>
						)}
						<RightContainer
							css={css`
								background-color: ${theme.colors.secondary.background};
								margin-bottom: 10px;
							`}
						>
							<Typography
								variant="div"
								size="sm"
								css={css`
									margin-bottom: 10px;
								`}
							>
								{content.contactQuestion}
							</Typography>
							<ButtonLink
								linkType="internal"
								to={content.contactLinkUrl}
								type="secondary"
								label={content.contactLinkLabel}
								orientation="right"
								size="sm"
							/>
						</RightContainer>
						<RightContainer
							css={css`
								background-color: ${theme.colors.primary.darkerGray};
								margin-bottom: 10px;
							`}
						>
							<Typography
								variant="h3"
								type="tertiary"
								css={css`
									margin-bottom: 20px;
									font-size: 20px;
								`}
							>
								{content.upcomingLabel}
							</Typography>
							{upcomingEventsToDisplay.map((event: any, index: number) => {
								return (
									<React.Fragment key={index}>
										<Divider
											css={css`
												color: ${theme.colors.primary.white};
												margin-top: 0px;
												margin-bottom: 0px;
											`}
										/>
										<UpcomingEvent>
											<UpcomingEventDetails>
												<Flex
													direction="column"
													justifyContent="space-between"
													css={css`
														height: 100%;
													`}
												>
													<FlexItem>
														<Typography
															variant="div"
															weight="bold"
															size="md"
															color={theme.colors.tertiary.header}
														>
															{event?.title}
														</Typography>
													</FlexItem>
													<FlexItem>
														<Typography
															variant="div"
															size="xs"
															type="tertiary"
														>
															{event?.eventDates}
														</Typography>
													</FlexItem>
												</Flex>
											</UpcomingEventDetails>
											<UpcomingEventImage
												css={css`
													background-image: url(${event?.heroImage?.file
														? event.heroImage?.file?.url
														: ""});
												`}
											/>
										</UpcomingEvent>
									</React.Fragment>
								);
							})}
							<Divider
								css={css`
									color: ${theme.colors.primary.white};
									margin-top: 0px;
									margin-bottom: 18px;
								`}
							/>
							<ButtonLink
								to={content.allEventsUrl}
								linkType="internal"
								type="tertiary"
								orientation="right"
								size="sm"
								label={content.allEventsLabel}
							/>
						</RightContainer>
						<PopularContent
							title={content.relatedLabel}
							blogContent={contentfulBlogEntries}
							excludeSlug={""}
						/>
						<Spacer
							css={css`
								height: 10px;
							`}
						/>
						<StayTunedComponent parent={stayTunedParent} slim={true} />
					</Right>
					<Spacer
						css={css`
							height: 140px;
						`}
					/>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary" vPadding="28px">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						css={css`
							margin-top: 14px;
							margin-bottom: 24px;
							letter-spacing: -2px;
						`}
						type="tertiary"
					>
						{contentfulEventDetailsForLanguage.title}
					</Typography>
					{contentfulEventDetailsForLanguage.description &&
					contentfulEventDetailsForLanguage.description.length > 0 ? (
						<Typography
							css={css`
								margin-bottom: 22px;
								font-size: 20px;
								letter-spacing: -1px;
								margin-top: 0px;
							`}
							type="tertiary"
							size="xl"
							color={theme.colors.tertiary.header}
							variant="h3"
						>
							{contentfulEventDetailsForLanguage.description}
						</Typography>
					) : (
						<></>
					)}
					{contentfulEventDetailsForLanguage.callToAction &&
					contentfulEventDetailsForLanguage.callToAction?.length > 0 ? (
						<Typography type="tertiary" size="md" variant="div">
							{contentfulEventDetailsForLanguage.callToAction}
						</Typography>
					) : (
						<></>
					)}
					{contentfulEventDetailsForLanguage.thirdPartyLinkLabel &&
					contentfulEventDetailsForLanguage.thirdPartyLinkUrl ? (
						<Typography
							css={css`
								margin-bottom: 26px;
							`}
							variant="div"
							size="md"
							type="tertiary"
						>
							{content.moreInformation}{" "}
							<Link
								next={false}
								to={contentfulEventDetailsForLanguage.thirdPartyLinkUrl}
								type="tertiary"
							>
								{contentfulEventDetailsForLanguage.thirdPartyLinkLabel}
							</Link>
						</Typography>
					) : (
						<></>
					)}
					{contentfulEventDetailsForLanguage.externalAction &&
					contentfulEventDetailsForLanguage.externalActionUrl ? (
						<ButtonLink
							orientation="right"
							type="tertiary"
							label={contentfulEventDetailsForLanguage.externalAction}
							to={contentfulEventDetailsForLanguage.externalActionUrl}
							next={false}
							size="sm"
							linkType="external"
						/>
					) : (
						<></>
					)}
					<MobileHeroImage
						css={css`
							background-image: url(${contentfulEventDetailsForLanguage.heroImage
								?.file
								? contentfulEventDetailsForLanguage.heroImage?.file?.url
								: ""});
							margin-bottom: 16px;
						`}
					/>
				</ContentSection>
				<ContentSection type="primary" vPadding="16px">
					{contentfulEventDetailsForLanguage.internalAction ? (
						<>
							<Typography
								variant="h3"
								type="primary"
								css={css`
									font-size: 20px;
									letter-spacing: -1px;
								`}
							>
								{content.rsvp}
							</Typography>
							<Divider
								css={css`
									margin-top: 16px;
									margin-bottom: 24px;
								`}
							/>
							<Typography
								variant="div"
								size="sm"
								type="primary"
								css={css`
									margin-bottom: 18px;
								`}
							>
								{contentfulEventDetailsForLanguage.internalAction}
							</Typography>
							{contentfulEventDetailsForLanguage.internalActionUrl && (
								<Link to={contentfulEventDetailsForLanguage.internalActionUrl}>
									<Button
										css={css`
											margin-bottom: 40px;
										`}
										fullwidth={true}
										label={content.rsvp + " " + content.withRyerson}
										onClick={() => {}}
									/>
								</Link>
							)}
						</>
					) : (
						<></>
					)}
					<SocialMedia
						color={theme.colors.primary.primaryBrand}
						type="primary"
						orientation="horizontal"
					/>
					<Divider
						css={css`
							margin-top: 8px;
							margin-bottom: 50px;
							color: ${theme.colors.primary.darkerGray};
						`}
					/>
					<Accordion type="primary" multiple={true}>
						<AccordionTab circled={true} header={content.generalTitle}>
							<ContentSection type="primary" vPadding="15px" hPadding="0px">
								<Typography
									variant="div"
									type="primary"
									css={css`
										margin-bottom: 5px;
									`}
									size="md"
								>
									{content.dateLabel}
									{": "}
									<Typography
										variant="span"
										weight="bold"
										color={theme.colors.primary.header}
									>
										{contentfulEventDetailsForLanguage.eventDates}
									</Typography>
								</Typography>
								<Typography
									variant="div"
									css={css`
										margin-bottom: 16px;
									`}
									type="primary"
									size="md"
								>
									{content.locationLabel}
									{": "}
									<Typography
										variant="span"
										weight="bold"
										color={theme.colors.primary.header}
									>
										{contentfulEventDetailsForLanguage.eventLocation}
									</Typography>
								</Typography>
								{contentfulEventDetailsForLanguage.mapLocation &&
								contentfulEventDetailsForLanguage.mapLocation.lon &&
								contentfulEventDetailsForLanguage.mapLocation.lat ? (
									<MapContainer
										css={css`
											height: 300px;
											margin-bottom: 25px;
										`}
									>
										<Map
											googleKey={
												process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY ?? ""
											}
											mapData={[
												{
													location: {
														lat: contentfulEventDetailsForLanguage
															.mapLocation.lat,
														lng: contentfulEventDetailsForLanguage
															.mapLocation.lon,
													},
													name: contentfulEventDetailsForLanguage.eventLocation,
													id: contentfulEventDetailsForLanguage.eventLocation,
												},
											]}
											zoomLevel={16}
										/>
									</MapContainer>
								) : (
									<></>
								)}
							</ContentSection>
						</AccordionTab>
					</Accordion>
					<Divider
						css={css`
							color: ${theme.colors.primary.darkerGray};
							margin-bottom: 24px;
						`}
					/>
					{/* Empty click handler and no link: tapping it does nothing. */}
					<Button fullwidth={true} label={content.registerToday} onClick={() => {}} />
				</ContentSection>
				<ContentSection type="secondary" vPadding="24px">
					<Typography variant="div" type="secondary" size="md">
						{content.contactQuestion}
					</Typography>
					<ButtonLink
						type="secondary"
						size="sm"
						linkType="internal"
						to={content.contactLinkUrl}
						label={content.contactLinkLabel}
						orientation="right"
					/>
				</ContentSection>
				<RightContainer
					css={css`
						background-color: ${theme.colors.primary.darkerGray};
						padding: 16px 21px 21px 21px;
						border-radius: 0px;
					`}
				>
					<Typography
						variant="h3"
						type="tertiary"
						css={css`
							margin-bottom: 20px;
							font-size: 20px;
						`}
					>
						{content.upcomingLabel}
					</Typography>
					{upcomingEventsToDisplay.map((event: any, index: number) => {
						return (
							<React.Fragment key={index}>
								<Divider
									css={css`
										color: ${theme.colors.primary.white};
										margin-top: 0px;
										margin-bottom: 0px;
									`}
								/>
								<UpcomingEvent>
									<UpcomingEventDetails
										css={css`
											height: 100px;
										`}
									>
										<Flex
											direction="column"
											justifyContent="space-between"
											css={css`
												height: 100%;
											`}
										>
											<FlexItem>
												<Typography
													variant="div"
													weight="bold"
													size="md"
													color={theme.colors.tertiary.header}
												>
													{event?.title}
												</Typography>
											</FlexItem>
											<FlexItem>
												<Typography variant="div" size="sm" type="tertiary">
													{event?.eventDates}
												</Typography>
											</FlexItem>
										</Flex>
									</UpcomingEventDetails>
									<UpcomingEventImage
										css={css`
											background-image: url(${event.heroImage?.file
												? event.heroImage?.file?.url
												: ""});
											height: 100px;
										`}
									/>
								</UpcomingEvent>
							</React.Fragment>
						);
					})}
					<Divider
						css={css`
							color: ${theme.colors.primary.white};
							margin-top: 0px;
							margin-bottom: 18px;
						`}
					/>
					<ButtonLink
						to={content.allEventsUrl}
						linkType="internal"
						type="tertiary"
						orientation="right"
						size="sm"
						label={content.allEventsLabel}
					/>
				</RightContainer>
				<Spacer
					css={css`
						height: 10px;
					`}
				/>
				<RightContainer>
					<PopularContent
						title={content.relatedLabel}
						blogContent={contentfulBlogEntries}
						excludeSlug={""}
					/>
				</RightContainer>
				<Spacer
					css={css`
						height: 10px;
					`}
				/>
				<ContentSection type="tertiary" vPadding="0px" hPadding="0px">
					<StayTunedComponent parent={stayTunedParent} />
				</ContentSection>
			</Media>
		</>
	);
};

export default EventDetails;
